"use client";
import { jsx } from "react/jsx-runtime";
import { cachingFetcher } from "../agent/cachingFetcher.js";
import { createContext, memo, useState, useEffect, useContext } from "react";
import "../../components/Chrome/BrowseContext/ContextProvider.js";
import { useBrowseContext } from "../../components/Chrome/BrowseContext/hook.js";
import { findMatchingRuleByAttributes } from "../../components/Scaffolding/SegmentBasedEntry/utils.js";
import { isAdobeScriptReady } from "../../hooks/useAdobe/index.js";
const AJOPersonalizationContext = createContext(null);
const AJOPersonalizationProviderClientComponent = ({
  children,
  locale,
  APIBasePath = "",
  AJOSurfaces,
  isOneTrustTargetingEnabled,
  isCookiesFullyLoaded
}) => {
  const [AJOCampaignInformation, setAJOCampaignInformation] = useState([]);
  const [isAdobeScriptLoaded, setAdobeIsScriptLoaded] = useState(isAdobeScriptReady);
  const getPersonalizedEntry = async function getPersonalizedEntry2({
    contentType,
    entryId
  }) {
    var _a;
    let APIEndpoint = `/api/contentStack/${locale}/${contentType}/getEntryById/${entryId}`;
    if (APIBasePath) {
      const pathname = (_a = window == null ? void 0 : window.location) == null ? void 0 : _a.pathname;
      if (pathname == null ? void 0 : pathname.includes(APIBasePath)) {
        APIEndpoint = `${APIBasePath}${APIEndpoint}`;
      }
    }
    const entry = await cachingFetcher(APIEndpoint, {}, 5e3, false, 18e3);
    return entry;
  };
  const getPersonalizedEntryFromAJO = async function getPersonalizedEntryFromAJO2({
    entry
  }) {
    var _a, _b, _c, _d, _e;
    let personalizedEntryId = (_b = (_a = entry.base_content_reference) == null ? void 0 : _a[0]) == null ? void 0 : _b.uid;
    let personalizedEntryContentType = (_d = (_c = entry.base_content_reference) == null ? void 0 : _c[0]) == null ? void 0 : _d._content_type_uid;
    if (isAdobeScriptLoaded && (AJOCampaignInformation == null ? void 0 : AJOCampaignInformation.length) > 0) {
      let matchingEntry;
      for (let i = 0; i < (AJOCampaignInformation == null ? void 0 : AJOCampaignInformation.length); i++) {
        if (((_e = entry.ajo_target_rules_block) == null ? void 0 : _e.length) > 0) {
          const matchingReference = findMatchingRuleByAttributes(
            entry.ajo_target_rules_block,
            AJOCampaignInformation[i]
          );
          if (matchingReference) {
            matchingEntry = matchingReference;
            break;
          }
        }
      }
      if (matchingEntry) {
        personalizedEntryId = matchingEntry.uid;
        personalizedEntryContentType = matchingEntry._content_type_uid;
      }
    }
    if (!personalizedEntryId) {
      return;
    }
    const personalizedEntry = await getPersonalizedEntry({
      contentType: personalizedEntryContentType,
      entryId: personalizedEntryId
    });
    if (!personalizedEntry) {
      return;
    }
    return {
      personalizedEntry,
      contentType: personalizedEntryContentType
    };
  };
  useEffect(() => {
    const handler = async () => {
      try {
        const AJOResponse = await adobe.ajo.getCampaignBySurfaces(AJOSurfaces);
        setAJOCampaignInformation(AJOResponse);
      } catch (_) {
      }
      !isAdobeScriptLoaded && setAdobeIsScriptLoaded(true);
    };
    document.addEventListener("at-library-loaded", handler);
    return () => {
      document.removeEventListener("at-library-loaded", handler);
    };
  }, [isAdobeScriptLoaded, AJOSurfaces]);
  return /* @__PURE__ */ jsx(
    AJOPersonalizationContext.Provider,
    {
      value: {
        isAdobeScriptLoaded,
        getPersonalizedEntry,
        getPersonalizedEntryFromAJO,
        isOneTrustTargetingEnabled,
        isCookiesFullyLoaded
      },
      children
    }
  );
};
const MemoizedCriteoProviderComponent = memo(
  AJOPersonalizationProviderClientComponent
);
const AJOPersonalizationProviderClient = (props) => {
  const { thirdPartyInfo } = useBrowseContext();
  const { consentManagement, isCookiesFullyLoaded } = thirdPartyInfo;
  const { isTargetingEnabled: isConsentManagementTargetingEnabled } = consentManagement;
  return /* @__PURE__ */ jsx(
    MemoizedCriteoProviderComponent,
    {
      isCookiesFullyLoaded,
      isOneTrustTargetingEnabled: isConsentManagementTargetingEnabled,
      ...props
    }
  );
};
const useAJOPersonalization = () => {
  const context = useContext(AJOPersonalizationContext);
  if (!context) {
    return {
      getPersonalizedEntry: async () => {
        return void 0;
      },
      getPersonalizedEntryFromAJO: async () => {
        return void 0;
      }
    };
  }
  return context;
};
function usePersonalizedEntryFromAJO({
  entry
}) {
  const [clientSideEntry, setClientSideEntry] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const {
    isAdobeScriptLoaded,
    getPersonalizedEntryFromAJO,
    isOneTrustTargetingEnabled,
    isCookiesFullyLoaded
  } = useAJOPersonalization();
  useEffect(() => {
    if (entry) {
      const getEntry = async () => {
        setIsLoading(true);
        try {
          const entryData = await getPersonalizedEntryFromAJO({
            entry
          });
          setClientSideEntry(entryData);
        } catch (err) {
          console.log({ err });
        }
        setIsLoading(false);
      };
      if (isAdobeScriptLoaded) {
        getEntry();
      } else if (isCookiesFullyLoaded && !isOneTrustTargetingEnabled) {
        setIsLoading(false);
      }
    }
  }, [
    entry,
    isAdobeScriptLoaded,
    isOneTrustTargetingEnabled,
    isCookiesFullyLoaded,
    getPersonalizedEntryFromAJO
  ]);
  return {
    isLoading,
    entryData: clientSideEntry
  };
}
export {
  AJOPersonalizationProviderClient,
  useAJOPersonalization,
  usePersonalizedEntryFromAJO
};
